/**
 * 🔐 O SEGUNDO FATOR NO NAVEGADOR — um ponto só para falar com o MFA nativo do Supabase Auth (TOTP).
 *
 * `@supabase/supabase-js` 2.106: `auth.mfa.enroll` · `challengeAndVerify` · `listFactors` · `unenroll` ·
 * `getAuthenticatorAssuranceLevel`. O segredo e o QR saem do Supabase e vão direto para a tela (uma vez,
 * só durante o cadastro); o código de 6 números vai do teclado direto para o Supabase. Nada disso passa
 * pela nossa API nem pelo nosso banco.
 *
 * O Supabase NÃO oferece códigos de recuperação para TOTP: quem perde o celular é destravado por um admin
 * da Regula (rota `/regula/duas-etapas/destravar`, com registro na trilha).
 *
 * `cliente` é injetável para o teste usar um dublê (nunca se cria fator de verdade em teste).
 */
import { supabase } from "../hooks/useAuth";

export type FatorTotp = { id: string; status: string; friendly_name?: string; created_at?: string; updated_at?: string };

type Mfa = {
  listFactors: () => Promise<{ data: { totp?: FatorTotp[]; all?: FatorTotp[] } | null; error: { message: string } | null }>;
  enroll: (p: { factorType: "totp"; friendlyName?: string; issuer?: string }) => Promise<{
    data: { id: string; totp: { qr_code: string; secret: string; uri: string } } | null;
    error: { message: string } | null;
  }>;
  challengeAndVerify: (p: { factorId: string; code: string }) => Promise<{ data: unknown; error: { message: string } | null }>;
  unenroll: (p: { factorId: string }) => Promise<{ data: unknown; error: { message: string } | null }>;
  getAuthenticatorAssuranceLevel: () => Promise<{ data: { currentLevel: string | null; nextLevel: string | null } | null; error: { message: string } | null }>;
};

let mfaDoTeste: Mfa | null = null;
/** Só para teste: troca o MFA do Supabase por um dublê (null volta ao real). */
export function _usarMfaDeTeste(m: Mfa | null): void {
  mfaDoTeste = m;
}
const mfa = (): Mfa => mfaDoTeste ?? (supabase.auth.mfa as unknown as Mfa);

/** O nome que aparece no app autenticador do celular. */
export const EMISSOR_NO_APP = "Regula360";

/** Os fatores da conta: os que já valem (verificados) e os cadastros que ficaram no meio. */
export async function listarFatores(): Promise<{ verificados: FatorTotp[]; pendentes: FatorTotp[] }> {
  const { data, error } = await mfa().listFactors();
  if (error) throw new Error(error.message);
  const todos = (data?.all ?? data?.totp ?? []).filter((f) => (f as { factor_type?: string }).factor_type === undefined || (f as { factor_type?: string }).factor_type === "totp");
  return { verificados: todos.filter((f) => f.status === "verified"), pendentes: todos.filter((f) => f.status !== "verified") };
}

/**
 * Passo 2: pede ao Supabase um fator novo e devolve o QR (imagem SVG em data URL) e a chave.
 * Antes, apaga cadastros que ficaram no meio (o Supabase recusa nome repetido e tem teto de fatores).
 */
export async function iniciarCadastro(): Promise<{ fatorId: string; qr: string; chave: string }> {
  const { pendentes } = await listarFatores();
  for (const f of pendentes) await mfa().unenroll({ factorId: f.id });
  const { data, error } = await mfa().enroll({ factorType: "totp", friendlyName: `${EMISSOR_NO_APP} ${Date.now().toString(36)}`, issuer: EMISSOR_NO_APP });
  if (error || !data) throw new Error(error?.message ?? "O cadastro não respondeu.");
  return { fatorId: data.id, qr: data.totp.qr_code, chave: data.totp.secret };
}

/** Passo 3 (e o login): confere o código no Supabase. Deu certo, a sessão sobe para `aal2`. */
export async function confirmarCodigo(fatorId: string, codigo: string): Promise<void> {
  const { error } = await mfa().challengeAndVerify({ factorId: fatorId, code: codigo });
  if (error) throw new Error(/invalid|expired|totp/i.test(error.message) ? "Código não confere. Confira o relógio do celular e digite o código que está na tela agora." : error.message);
}

/** Desligar: apaga o fator (o Supabase exige sessão `aal2` para apagar um fator verificado). */
export async function desativar(fatorId: string): Promise<void> {
  const { error } = await mfa().unenroll({ factorId: fatorId });
  if (error) throw new Error(error.message);
}

/** O nível da sessão agora e o que ela pode alcançar (`aal2` quando a conta tem fator verificado). */
export async function nivelDaSessao(): Promise<{ atual: string | null; proximo: string | null }> {
  const { data, error } = await mfa().getAuthenticatorAssuranceLevel();
  if (error) throw new Error(error.message);
  return { atual: data?.currentLevel ?? null, proximo: data?.nextLevel ?? null };
}

/** A conta tem fator e a sessão ainda não confirmou o código: o login tem que pedir o código. */
export function precisaDoCodigo(n: { atual: string | null; proximo: string | null } | null): boolean {
  return !!n && n.proximo === "aal2" && n.atual !== "aal2";
}
